/**
 * ResultsDisplay Component
 * Displays calculation results, recommendations, and affiliate links
 */

import { api } from '../services/api.js';
import { showError, showSuccess } from '../utils/toast.js';

export class ResultsDisplay {
  constructor(containerId, onStartOver) {
    this.container = document.getElementById(containerId);
    this.onStartOver = onStartOver;
    this.data = null;
    this.formData = null; // Store form data for PDF download
  }

  setFormData(formData) {
    this.formData = formData;
  }

  render(data) {
    this.data = data;
    
    if (!data) {
      this.container.innerHTML = '<div class="card"><p>No results to display.</p></div>';
      return;
    }

    const { 
      installation_instructions = [], 
      mounting_specifications,
      recommended_projector,
      recommendations = []
    } = data;

    this.container.innerHTML = `
      <div class="results-container">
        <div class="results-header">
          <h2 class="results-title">Installation Instructions</h2>
          <p class="results-subtitle">Follow these steps to mount your projector</p>
        </div>

        <!-- Installation Instructions - Front and Center -->
        <div class="instructions-section">
          <div class="instructions-card">
            <ul class="instructions-list">
              ${this.formatInstructions(installation_instructions)}
            </ul>
            <div class="pdf-download-section">
              <button class="btn btn-primary" id="download-pdf-btn">
                📄 Download Printable PDF
              </button>
            </div>
          </div>
        </div>

        <!-- Product Recommendations - Smaller Cards -->
        <div class="recommendations-section">
          <h3 class="section-title">Recommended Products</h3>
          <div class="recommendations-grid-compact">
            ${recommendations.length > 0 
              ? recommendations.map((rec, index) => this.renderRecommendation(rec, index)).join('')
              : '<div class="card"><p>No specific recommendations available at this time.</p></div>'
            }
          </div>
        </div>

        <div class="results-actions">
          <button class="btn btn-primary" id="start-over-btn">Start Over</button>
        </div>
      </div>
    `;

    // Add event listener for PDF download button
    const downloadPdfBtn = this.container.querySelector('#download-pdf-btn');
    if (downloadPdfBtn) {
      downloadPdfBtn.addEventListener('click', () => this.downloadPDF());
    }

    // Add event listener for start over button
    const startOverBtn = this.container.querySelector('#start-over-btn');
    if (startOverBtn && this.onStartOver) {
      startOverBtn.addEventListener('click', () => {
        if (this.onStartOver) {
          this.onStartOver();
        }
      });
    }
  }

  async downloadPDF() {
    if (!this.formData) {
      showError('Form data not available. Please recalculate recommendations.');
      return;
    }

    try {
      // Prepare payload with form data
      const payload = {
        length: this.formData.table.length,
        width: this.formData.table.width,
        height: this.formData.table.height,
        ceiling_height: this.formData.room.ceilingHeight,
        mounting_location: this.formData.room.mountingOption || null,
        material: this.formData.table.material || null,
        location: this.formData.table.location || null,
        // Room considerations
        has_obstructions: this.formData.room.hasObstructions || null,
        needs_cable_management: this.formData.room.needsCableManagement || null,
        has_multiple_tables: this.formData.room.hasMultipleTables || null,
        // Preferences
        additional_notes: this.formData.preferences.additionalNotes || null,
      };

      // Make request with blob response
      const response = await fetch('/api/v0/download-instructions', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.detail || errorData.message || 'Failed to download PDF');
      }

      // Get blob and create download link
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'installation-instructions.pdf');
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      showSuccess('PDF downloaded successfully!');
    } catch (error) {
      console.error('Error downloading PDF:', error);
      showError(error.message || 'Failed to download PDF');
    }
  }

  formatInstructions(instructions) {
    if (!instructions || instructions.length === 0) {
      return '<li>No instructions available.</li>';
    }

    let html = '';
    let inSubList = false;

    instructions.forEach((inst, index) => {
      // Skip empty lines
      if (!inst || inst.trim() === '') {
        // Close sub-list if we were in one, then add spacing
        if (inSubList) {
          html += '</ul>';
          inSubList = false;
        }
        return; // Skip empty lines
      }
      
      // Check if this is a sub-bullet (starts with "  - ")
      const isSubBullet = inst.trim().startsWith('- ');
      
      if (isSubBullet) {
        // Start a sub-list if we haven't already
        if (!inSubList) {
          html += '<ul class="instructions-sublist">';
          inSubList = true;
        }
        // Remove the "  - " prefix and format
        const content = inst.trim().substring(2); // Remove "- "
        html += `<li>${this.formatBoldText(content)}</li>`;
      } else {
        // Close sub-list if we were in one
        if (inSubList) {
          html += '</ul>';
          inSubList = false;
        }
        // Regular bullet point
        html += `<li>${this.formatBoldText(inst)}</li>`;
      }
    });

    // Close sub-list if still open
    if (inSubList) {
      html += '</ul>';
    }

    return html;
  }

  formatBoldText(text) {
    // Convert **text** to <strong>text</strong>
    return text.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>');
  }

  renderRecommendation(rec, index) {
    const priorityClass = rec.priority?.toLowerCase() || 'medium';
    const priorityLabel = {
      high: 'High Priority',
      medium: 'Medium Priority',
      low: 'Low Priority'
    }[priorityClass] || 'Recommended';

    return `
      <div class="recommendation-card-compact priority-${priorityClass}">
        <div class="recommendation-header">
          <h4 class="recommendation-title">${rec.item || 'Product'}</h4>
          <span class="recommendation-priority">${priorityLabel}</span>
        </div>
        <p class="recommendation-description">${rec.description || 'No description available.'}</p>
        ${rec.affiliate_link 
          ? `<a href="${rec.affiliate_link}" target="_blank" rel="noopener noreferrer" class="btn btn-secondary btn-sm recommendation-link">
              View Product →
            </a>`
          : ''
        }
      </div>
    `;
  }

  showLoading() {
    this.container.innerHTML = `
      <div class="loading-state">
        <div class="spinner"></div>
        <p class="loading-text">Calculating recommendations...</p>
      </div>
    `;
  }

  showError(message) {
    this.container.innerHTML = `
      <div class="error-state">
        <div class="error-icon">⚠️</div>
        <h3 class="error-title">Error Loading Results</h3>
        <p class="error-message">${message || 'An unexpected error occurred. Please try again.'}</p>
        <button class="btn btn-primary" id="retry-btn">Try Again</button>
      </div>
    `;

    const retryBtn = this.container.querySelector('#retry-btn');
    if (retryBtn && this.onStartOver) {
      retryBtn.addEventListener('click', () => {
        if (this.onStartOver) {
          this.onStartOver();
        }
      });
    }
  }
}

